import React, { useEffect, useState, useCallback } from "react";
import { fetchIngredients, fetchUsage, logUsage, fetchDailyIssue, confirmUsage, getRole } from "../services/api";
import { useT } from "../i18n/useT";

function todayLocal() {
  return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}
const SHIFTS = [1, 2, 3, 4];

// F&B Cost Control — per-shift usage with handover between chefs.
//   Shift 1 opens with the morning issue; each later shift opens with the
//   previous shift's handover (auto-chained, adjustable). Each chef records
//   Used and Handover; the last shift's handover is the return to the store.
//   Unaccounted loss (received − used − handover) is flagged per shift.
//   Chef accepts the handover; the controller confirms the whole day.
export default function UsagePage() {
  const { t } = useT();
  const role = getRole();
  const canConfirm = role === "controller" || role === "manager";
  const [date, setDate] = useState(todayLocal());
  const [shift, setShift] = useState(1);
  const [ings, setIngs] = useState([]);
  const [issued, setIssued] = useState({});     // ingredient_id -> issued qty (shift 1 opening)
  const [allShifts, setAllShifts] = useState({}); // shift -> { ing_id -> {received, used, handover, accepted} }
  const [dayConfirmed, setDayConfirmed] = useState(false);
  // Editable maps for the selected shift
  const [rec, setRec] = useState({});
  const [used, setUsed] = useState({});
  const [hand, setHand] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchIngredients().then(setIngs).catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load"))); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadDay = useCallback(() => {
    setLoading(true); setMsg("");
    Promise.all([fetchUsage(date), fetchDailyIssue(date).catch(() => null)])
      .then(([u, iss]) => {
        const bs = {};
        (u.usage || []).forEach((x) => {
          const s = x.shift || 1;
          (bs[s] = bs[s] || {})[x.ingredient_id] = {
            received: x.received_qty, used: x.quantity, handover: x.returned_qty,
            accepted: !!x.handover_accepted, closing: !!x.is_closing,
          };
        });
        setAllShifts(bs); setDayConfirmed(!!u.confirmed);
        const mi = {};
        if (iss && iss.lines) iss.lines.forEach((l) => { mi[l.ingredient_id] = l.received_qty != null ? l.received_qty : l.issued_qty; });
        setIssued(mi);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);
  useEffect(() => { loadDay(); }, [loadDay]);

  // Rebuild the editable maps whenever the shift or loaded data changes.
  const defaultReceived = useCallback((iid) => {
    if (shift === 1) return issued[iid];
    const prev = allShifts[shift - 1];
    return prev && prev[iid] ? prev[iid].handover : undefined;
  }, [shift, issued, allShifts]);

  useEffect(() => {
    if (!ings.length) return;
    const cur = allShifts[shift] || {};
    const mr = {}, mu = {}, mh = {};
    ings.forEach((i) => {
      const st = cur[i.id];
      const def = defaultReceived(i.id);
      mr[i.id] = st && st.received != null ? String(st.received) : (def != null ? String(def) : "");
      mu[i.id] = st && st.used != null ? String(st.used) : "";
      mh[i.id] = st && st.handover != null ? String(st.handover) : "";
    });
    setRec(mr); setUsed(mu); setHand(mh);
    setAccepted(Object.values(cur).some((x) => x.accepted));
    setClosing(Object.values(cur).some((x) => x.closing));
  }, [shift, allShifts, issued, ings, defaultReceived]);

  const num = (v) => (v === undefined || v === "" ? NaN : Number(v));
  const lossFor = (i) => {
    const r = num(rec[i.id]);
    if (isNaN(r)) return null;
    const u = num(used[i.id]); const h = num(hand[i.id]);
    if (isNaN(u) && isNaN(h)) return null;
    return Math.round((r - (isNaN(u) ? 0 : u) - (isNaN(h) ? 0 : h)) * 1000) / 1000;
  };
  const rowsToSend = () => ings
    .filter((i) => [rec[i.id], used[i.id], hand[i.id]].some((v) => v !== undefined && v !== ""))
    .map((i) => ({
      ingredient_id: i.id, used_on: date, shift,
      received_qty: (rec[i.id] === undefined || rec[i.id] === "") ? null : Number(rec[i.id]),
      quantity: Number(used[i.id]) || 0,
      returned_qty: (hand[i.id] === undefined || hand[i.id] === "") ? null : Number(hand[i.id]),
      handover_accepted: accepted, is_closing: closing,
    }));

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      for (const l of rowsToSend()) await logUsage(l);
      setDayConfirmed(false);
      setMsg(t("cc_saved_usage_for").replace("{date}", date));
      loadDay();
    } catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_save")); }
    finally { setSaving(false); }
  };

  const confirm = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      for (const l of rowsToSend()) await logUsage(l);
      await confirmUsage({ used_on: date });
      setDayConfirmed(true);
      setMsg(t("cc_confirmed"));
      loadDay();
    } catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_save")); }
    finally { setSaving(false); }
  };

  return (
    <>
      <h1>🍳 {t("cc_nav_usage")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 640 }}>{t("cc_usage_intro")}</p>

      <div className="card" style={{ marginTop: 12, maxWidth: 660 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_day")}<br />
            <input type="date" value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 10,
            background: dayConfirmed ? "#e7f6ec" : "#fff4e0", color: dayConfirmed ? "#1D9E75" : "#854F0B" }}>
            {dayConfirmed ? "✓ " + t("cc_confirmed") : t("cc_pending")}
          </span>
        </div>

        {/* Shift selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#5f6b78", fontWeight: 600 }}>{t("cc_shift")}:</span>
          {SHIFTS.map((s) => (
            <button key={s} className={"btn small " + (shift === s ? "" : "secondary")} onClick={() => setShift(s)}>{s}</button>
          ))}
          {shift > 1 && (
            <label style={{ fontSize: 12, color: "#3e4c59", display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              {t("cc_accept_handover")}
            </label>
          )}
          <label style={{ fontSize: 12, fontWeight: 600, color: closing ? "#008080" : "#3e4c59", display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
            <input type="checkbox" checked={closing} onChange={(e) => setClosing(e.target.checked)} />
            {t("cc_closing_shift")}
          </label>
        </div>
        <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>{t("cc_shift_hint")}</p>

        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
        {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : ings.length === 0 ? (
          <p className="muted" style={{ marginTop: 12 }}>{t("cc_no_ings_defined")}</p>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 92px 92px 92px", gap: 8, fontSize: 11, color: "#5f6b78", fontWeight: 700, textTransform: "uppercase", paddingBottom: 4, borderBottom: "1px solid #eef1f3" }}>
              <span>{t("cc_ingredient")}</span>
              <span style={{ textAlign: "right" }}>{t("cc_received")}</span>
              <span style={{ textAlign: "right" }}>{t("cc_used")}</span>
              <span style={{ textAlign: "right", color: closing ? "#008080" : undefined }}>{closing ? t("cc_return_to_store") : t("cc_handover")}</span>
            </div>
            {ings.map((i) => {
              const loss = lossFor(i);
              return (
                <div key={i.id} style={{ display: "grid", gridTemplateColumns: "1fr 92px 92px 92px", gap: 8, alignItems: "center", padding: "5px 0" }}>
                  <span>{i.name} <span className="muted" style={{ fontSize: 11 }}>({i.unit})</span>
                    {loss != null && Math.abs(loss) >= 0.001 && <span style={{ fontSize: 11, marginLeft: 6, color: "#A32D2D", fontWeight: 600 }}>· {t("cc_loss")}: {loss}</span>}
                  </span>
                  <input type="number" min="0" step="0.001" value={rec[i.id] ?? ""} onChange={(e) => setRec({ ...rec, [i.id]: e.target.value })} placeholder="0" style={{ textAlign: "right" }} />
                  <input type="number" min="0" step="0.001" value={used[i.id] ?? ""} onChange={(e) => setUsed({ ...used, [i.id]: e.target.value })} placeholder="0" style={{ textAlign: "right" }} />
                  <input type="number" min="0" step="0.001" value={hand[i.id] ?? ""} onChange={(e) => setHand({ ...hand, [i.id]: e.target.value })} placeholder="0" style={{ textAlign: "right" }} />
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
              <button className="btn" onClick={save} disabled={saving}>{saving ? t("cc_saving") : t("cc_save_usage")}</button>
              {canConfirm && <button className="btn ok" onClick={confirm} disabled={saving}>✓ {t("cc_confirm_usage")}</button>}
              {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
